import { ScopeTreeComponent } from '@/src/app/features/user/components/scope-tree/scope-tree.component';
import { Scope } from '@/src/app/features/user/models/scope.model';
import { Profile } from '@/src/app/features/user/models/user-data.model';
import { UserService } from '@/src/app/features/user/services/user.service';
import { ScopeType } from '@/src/app/features/user/scope-type.enum';
import { SelectOption, SelectOptions } from '@shared/components/form/components/select/select.model';
import { InstanceArea } from '@/src/app/shared/models/stxconfiguration.model';
import { StxValidators } from '@/src/app/shared/validation/validators';
import { OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { merge } from 'rxjs';
import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Item } from 'src/app/shared/models/item.model';
import { WsHelperService } from '@shared/services/ws-helper.service';
import { surgicalQaReviewerSelectValue, qaReviewerRoleIds, chinaScopeId } from '@utils/constants.utils';

import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatCheckbox } from '@angular/material/checkbox';
import { ScopeTreeComponent as ScopeTreeComponent_1 } from '../scope-tree/scope-tree.component';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AutocompleteSingleSelectComponent } from '@shared/components/autocomplete-single-select/autocomplete-single-select.component';

@Component({
  selector: 'stx-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox,
    ScopeTreeComponent_1,
    MatFormField,
    MatInput,
    MatError,
    TranslateModule,
    AutocompleteSingleSelectComponent
  ]
})
export class UserProfileComponent implements OnInit, OnChanges {
  private userService = inject(UserService);
  private cd = inject(ChangeDetectorRef);
  private readonly wsHelperService = inject(WsHelperService);

  roles: Item[];
  geoScopes: Scope[];
  orgTreeScopes: Scope[];
  checkedOrg: Scope[];
  checkedGeo: Scope[];
  instanceAreaList: SelectOptions;
  readonly maximumProfileNameLength: number = 255;
  profileForm = new UntypedFormGroup({
    selectedRole: new UntypedFormControl(null, StxValidators.required),
    isDomainAccess: new UntypedFormControl(false),
    profileNameFormControl: new UntypedFormControl(null, Validators.maxLength(this.maximumProfileNameLength)),
    designatedArea: new UntypedFormControl(InstanceArea.GLOBAL, StxValidators.required)
  });

  get isDomainAccess(): boolean {
    return this.profileForm.controls.isDomainAccess.value;
  }
  isGeoScopeDisabled = false;
  activeProfileForLogUser: Profile;
  userProfile: Profile;
  domainScope: Scope;
  @ViewChild('geotree') geoTreeComponent: ScopeTreeComponent;
  @ViewChild('orgtree') orgTreeComponent: ScopeTreeComponent;
  @Input() isEditingExistingProfile = false;
  @Input()
  set profile(profile: Profile) {
    if (profile) {
      const domainScopeId = 1;
      this.userProfile = profile;
      this.profileForm.patchValue({
        selectedRole: profile.role.id,
        profileNameFormControl: profile.name,
        designatedArea: profile.designatedArea
      });

      this.checkedGeo = profile.scopes?.filter(scope => scope.type === ScopeType.COUNTRY || scope.type === ScopeType.REGION) ?? [];
      this.checkedOrg =
        profile.scopes?.filter(scope => scope.type === ScopeType.PARTNER || scope.type === ScopeType.TREATMENT_CENTER) ?? [];

      if (profile.scopes?.length > 0 && profile.scopes[0].id === domainScopeId) {
        this.profileForm.controls.isDomainAccess.setValue(true);
      }

      this.cd.detectChanges();
    }
  }

  ngOnInit() {
    this.initializeInstanceAreaOptions();
    this.getUserData();
    this.setDomainAccessWhenSelectedSurgicalQaReviewer();
    this.setCountrySelectionWhenQaReviewerAndChinaInstance();
    this.setDomainAccessDisabledWhenSelectedChinaInstance();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isEditingExistingProfile && this.profileForm) {
      const control = this.profileForm.get('designatedArea');
      this.isEditingExistingProfile ? control.disable() : control.enable();
    }
  }

  initializeInstanceAreaOptions() {
    this.instanceAreaList = [
      {
        id: InstanceArea.GLOBAL,
        name: InstanceArea.GLOBAL
      } as SelectOption,
      {
        id: InstanceArea.CHINA,
        name: InstanceArea.CHINA
      } as SelectOption
    ];
    const designatedAreaControl = this.profileForm.controls.designatedArea;

    if (this.isEditingExistingProfile && this.userProfile?.designatedArea) {
      designatedAreaControl.patchValue(this.userProfile.designatedArea);
    } else {
      designatedAreaControl.patchValue(InstanceArea.GLOBAL);
    }
  }

  getUserData() {
    this.wsHelperService
      .callWithSpinner(
        forkJoin([
          this.userService.getRoles(),
          this.userService.getGeoScope(),
          this.userService.getOrgTreeScope(),
          this.userService.getDomainAccess()
        ])
      )
      .subscribe(result => {
        this.roles = result[0] as Item[];
        this.geoScopes = result[1] as Scope[];
        this.orgTreeScopes = result[2] as Scope[];
        this.domainScope = result[3] as Scope;
      });
  }

  private getScopeIds(): number[] {
    if (this.isDomainAccess) {
      return [this.domainScope.id];
    }
    return this.orgTreeComponent.getSelectionIds().concat(this.geoTreeComponent.getSelectionIds());
  }

  getProfile(): Profile {
    let profile: Profile = new Profile();
    profile.scopesId = this.getScopeIds();
    if (this.profileForm.valid && profile.scopesId.length > 0) {
      if (this.userProfile) {
        profile.id = this.userProfile.id;
      }
      profile.name = this.profileForm.controls.profileNameFormControl.value;
      profile.roleId = this.profileForm.controls.selectedRole.value;
      profile.designatedArea = this.profileForm.controls.designatedArea.value;
      return profile;
    }

    return null;
  }

  private getProfileFormField(field: string) {
    return this.profileForm.get(field);
  }

  private setDomainAccessWhenSelectedSurgicalQaReviewer() {
    const domainAccessControl = this.getProfileFormField('isDomainAccess');

    const updateDomainAccessControl = () => {
      const designatedArea = this.getProfileFormField('designatedArea').value;

      if (qaReviewerRoleIds.includes(this.getProfileFormField('selectedRole').value) && designatedArea === InstanceArea.CHINA) {
        domainAccessControl.setValue(false);
        domainAccessControl.disable();
      } else if (this.getProfileFormField('selectedRole').value === surgicalQaReviewerSelectValue) {
        domainAccessControl.setValue(true);
        domainAccessControl.disable();
      } else {
        domainAccessControl.setValue(false);
        domainAccessControl.enable();
      }
    };

    this.getProfileFormField('selectedRole').valueChanges.subscribe(updateDomainAccessControl);
    this.getProfileFormField('designatedArea').valueChanges.subscribe(updateDomainAccessControl);
  }

  private setDomainAccessDisabledWhenSelectedChinaInstance() {
    const domainAccessControl = this.getProfileFormField('isDomainAccess');
    const updateDomainAccessControlForProfile = () => {
      const designatedArea = this.getProfileFormField('designatedArea').value;
      if (designatedArea === InstanceArea.CHINA) {
        domainAccessControl.setValue(false);
        domainAccessControl.disable();
        return;
      }

      if (!surgicalQaReviewerSelectValue === this.getProfileFormField('selectedRole').value) {
        domainAccessControl.enable();
        return;
      }
    };

    merge(this.getProfileFormField('selectedRole').valueChanges, this.getProfileFormField('designatedArea').valueChanges).subscribe(
      updateDomainAccessControlForProfile
    );
    updateDomainAccessControlForProfile();
  }

  isProfileNameLengthNotValid(): boolean {
    return !this.getProfileFormField('profileNameFormControl').valid;
  }

  private setCountrySelectionWhenQaReviewerAndChinaInstance() {
    const toggleChinaSelection = () => {
      const chinaNode = this.geoTreeComponent.treeControl.dataNodes.find(x => x.scope.id === chinaScopeId);
      if (chinaNode && this.geoTreeComponent.checklistSelection.isSelected(chinaNode) !== this.isGeoScopeDisabled) {
        this.geoTreeComponent.checklistSelection.select(chinaNode);
      }
    };

    merge(this.getProfileFormField('selectedRole').valueChanges, this.getProfileFormField('designatedArea').valueChanges).subscribe(() => {
      const isQaReviewer = qaReviewerRoleIds.includes(this.getProfileFormField('selectedRole').value);
      const isChinaInstance = this.getProfileFormField('designatedArea').value === InstanceArea.CHINA;
      this.isGeoScopeDisabled = isQaReviewer && isChinaInstance;

      toggleChinaSelection();
    });
  }
}
